import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  type RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Args,
  type RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Options,
  useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_create,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import type { Client } from '@zg-rentals/http-client';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const assertMultiplexAmenityCreateMutationArgs = (
  args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Args,
): void => {
  const { getParams } = args;

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexAmenityCreate: `getParams` is required (CreateParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;
  const text = getParams.text;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityCreate: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof text !== 'string' || text.trim() === '') {
    throw new TypeError('useMultiplexAmenityCreate: `getParams.text` is required on every call (non-empty string).');
  }
};

export type UseMultiplexAmenityCreateOptions = Omit<
  RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexAmenityCreateMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_create
>;

export type UseMultiplexAmenityCreateResult = Omit<
  BaseMultiplexAmenityCreateMutation,
  'mutate' | 'mutateAsync' | 'error'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Args,
    options?: Parameters<BaseMultiplexAmenityCreateMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexAmenityCreateMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexAmenityWebApiHandler_create_Args,
    options?: Parameters<BaseMultiplexAmenityCreateMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexAmenityCreateMutation['mutateAsync']>;
  errors: ApiResponseErrorDto<unknown> | undefined;
};

/**
 * Multiplex additional-amenity create: wraps POST `/api/web/v1/users/properties/multiplex/amenities/create`.
 */
const useMultiplexAmenityCreate = (
  mutationOptions: UseMultiplexAmenityCreateOptions = {},
): UseMultiplexAmenityCreateResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_create({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexAmenityWebApiHandler_create',
        details: 'useMultiplexAmenityCreate',
        ...metaOverrides?.context,
      },
    },
  });

  const { error, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexAmenityCreateMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexAmenityCreateMutationArgs(args);
      return mutateAsync(args, options);
    },
    errors: toApiResponseErrorDto(error),
  };
};

export default useMultiplexAmenityCreate;
